import { useEffect, useRef } from 'react'
import { Linking } from 'react-native'
import { useMutation } from '@tanstack/react-query'
import { AppRoutes, AppStackScreenProps } from '@vatom/wallet-routes'
import { LoaderView, Status, Toast } from '@vatom/wombo'
import { observer } from 'mobx-react-lite'
import { match } from 'path-to-regexp'

import {
  useBusinessSelector,
  useInventoryByBusiness,
  withBusinessSelector
} from '../../hooks/useBusinessSelector'
import { useAquireMutation } from '../Acquire'

export type FindTokenProps = AppStackScreenProps<
  typeof AppRoutes.FindToken | typeof AppRoutes.FindToken_Business
>

const pathVariations = ['/acquire/:tokenId', '/b/:businessId/acquire/:tokenId']

export const extractTokenIdFromAcquireLink = (url: string): string | null => {
  for (const path of pathVariations) {
    const matcher = match<{
      tokenId: string
    }>(path, { decode: decodeURIComponent })

    const matched = matcher(url)
    if (matched) {
      return matched.params?.tokenId
    }
  }
  return null
}

export const FindTokenScreen = observer(({ route, navigation }: FindTokenProps) => {
  const params = route.params
  const inventory = useInventoryByBusiness()
  const { businessIdentifier } = useBusinessSelector()
  const aquireMutation = useAquireMutation()

  const claimTokenMutation = useMutation({
    mutationFn: async () => {
      const { autoClaim, sync } = params
      const objectDefinitionId =
        'objectDefinitionId' in params ? params.objectDefinitionId : undefined
      const campaignId = 'campaignId' in params ? params.campaignId : undefined
      const businessId = businessIdentifier

      if (!businessId) {
        console.error('Business is not ready')
        return
      }

      if (!campaignId && !objectDefinitionId) {
        // nothing to look for
        navigation.navigate(AppRoutes.BusinessProxy, { business: businessId })
        return
      }
      // Check if the user already has the token
      const token = inventory.tokens?.find(
        token => token.studioInfo?.objectDefinitionId === objectDefinitionId
      )

      if (token) {
        navigation.navigate(AppRoutes.NFTDetail_Business, {
          tokenId: token.id,
          business: businessId
        })
        return
      }
      // Check if the user has the token by campaignId
      if (campaignId) {
        const token = inventory.tokens?.find(token => token.studioInfo?.campaignId === campaignId)

        if (token) {
          navigation.navigate(AppRoutes.NFTDetail_Business, {
            tokenId: token.id,
            business: businessId
          })
          return
        }
      }
      if (!autoClaim) {
        // user doesn't have the token nor wants to claim it
        navigation.navigate(AppRoutes.home)
        return
      }
      const claimLink = 'claimLink' in params ? params.claimLink : undefined
      Toast({
        id: 'aquiring',
        title: `Aquiring...`,
        placement: 'top',
        status: Status.success
      })

      const shareToken = Buffer.from(
        JSON.stringify({
          objectDefinitionId,
          campaignId
        })
      ).toString('base64')

      const finalClaimLink =
        claimLink ?? `https://${businessId}.share.vatominc.com/emit/${shareToken}?json=true`
      if (finalClaimLink.includes('share.vatominc.com/emit/')) {
        const res = await fetch(finalClaimLink)
        const body = (await res.json()) as { acquireLink: string }
        const acquireLink = !sync ? body.acquireLink : `${body.acquireLink}&sync=true`
        const aquireUrl = new URL(acquireLink)
        const acquireParams = new URLSearchParams(aquireUrl.search)
        type MutationParams = Omit<
          Parameters<typeof aquireMutation.mutateAsync>[0],
          'id' | 'businessId'
        >
        const mutationParams = Object.fromEntries(acquireParams.entries()) as MutationParams

        const tokenId = extractTokenIdFromAcquireLink(aquireUrl.pathname)
        if (tokenId) {
          await aquireMutation.mutateAsync({
            id: tokenId,
            businessId: businessId,
            ...mutationParams
          })
        }

        // navigation.navigate(AppRoutes.NFTDetail_Business, { tokenId, business: businessId })
        // Hack: navigation call is unlikely to work unless the websocket comes extremely fast so we toggle the flag so the next mutation can run and find it
      } else {
        console.info('Performing standard claim')
        Linking.openURL(finalClaimLink)
      }
    }
  })

  const mutationDidRun = useRef(false)
  const navigationCallDidRun = useRef(false)

  useEffect(
    () => {
      if (inventory.isLoading) {
        return
      }

      const objectDefinitionId =
        'objectDefinitionId' in params ? params.objectDefinitionId : undefined
      const campaignId = 'campaignId' in params ? params.campaignId : undefined
      const businessId = businessIdentifier

      if (!businessId) {
        console.error('Business is not ready')
        return
      }
      if (navigationCallDidRun.current) {
        return
      }

      if (!campaignId && !objectDefinitionId) {
        // nothing to look for
        navigation.navigate(AppRoutes.BusinessProxy, { business: businessId })
        navigationCallDidRun.current = true
        return
      }
      // Check if the user already has the token
      const token = inventory.tokens?.find(
        token => token.studioInfo?.objectDefinitionId === objectDefinitionId
      )

      if (token) {
        navigationCallDidRun.current = true
        navigation.navigate(AppRoutes.NFTDetail_Business, {
          tokenId: token.id,
          business: businessId
        })

        return
      }
      // Check if the user has the token by campaignId
      if (campaignId) {
        const token = inventory.tokens?.find(token => token.studioInfo?.campaignId === campaignId)

        if (token) {
          navigationCallDidRun.current = true

          navigation.navigate(AppRoutes.NFTDetail_Business, {
            tokenId: token.id,
            business: businessId
          })
          return
        }
      }

      if (mutationDidRun.current) {
        return
      }
      mutationDidRun.current = true
      claimTokenMutation.mutate()

      // Claim the token
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inventory.isLoading, inventory.tokens]
  )

  return <LoaderView />
})

export const FindToken = withBusinessSelector(FindTokenScreen)
